import React from 'react';
import clsx from 'clsx';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

import { SearchPalette } from './search-palette';

type UniversalSearchContextValue = { open: () => void };

const UniversalSearchContext = React.createContext<UniversalSearchContextValue | null>(null);

/** Mac reports metaKey for Command; every other platform uses Control. */
const isPaletteShortcut = (event: KeyboardEvent) =>
  event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey);

/**
 * Owns the palette and the shortcut that opens it. Mounted once by the
 * dashboard, inside the tree that resolves the agency user, since every
 * section's query and access check needs it.
 */
export const UniversalSearchProvider = ({ children }: { children: React.ReactNode }) => {
  const [open, setOpen] = React.useState(false);
  const value = React.useMemo(() => ({ open: () => setOpen(true) }), []);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isPaletteShortcut(event)) {
        return;
      }
      // Claimed rather than passed through: in the browser's own binding
      // Cmd+K focuses the address bar, which would take the user out of Cara.
      event.preventDefault();
      setOpen((previous) => !previous);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <UniversalSearchContext.Provider value={value}>
      {children}
      <SearchPalette open={open} onClose={() => setOpen(false)} />
    </UniversalSearchContext.Provider>
  );
};

/**
 * Opens the palette. Returns a no-op outside the provider so a page rendered
 * on its own in a test does not have to stand one up.
 */
export const useSearchPalette = (): UniversalSearchContextValue =>
  React.useContext(UniversalSearchContext) ?? { open: () => {} };

/** Reads as Cmd on a Mac and Ctrl everywhere else, matching the live shortcut. */
export const useShortcutLabel = () => {
  const [mac, setMac] = React.useState(true);

  React.useEffect(() => {
    setMac(/Mac|iPhone|iPad/.test(window.navigator.platform || window.navigator.userAgent));
  }, []);

  return mac ? '⌘K' : 'Ctrl K';
};

/** The sidebar's search row, above the navigation groups. */
export const SearchRow = () => {
  const { open } = useSearchPalette();
  const shortcut = useShortcutLabel();

  return (
    <button
      type="button"
      onClick={open}
      className={clsx(
        'flex h-9 w-full items-center gap-2.5 rounded-md border border-neutral-100 dark:border-neutral-700 bg-neutral-50/60 dark:bg-neutral-900/60 px-2.5 text-left',
        'text-sm font-medium text-neutral-400 dark:text-neutral-500 transition-colors duration-150 ease-[cubic-bezier(0.2,0.8,0.2,1)]',
        'hover:border-neutral-200 dark:hover:border-white/10 hover:bg-white dark:hover:bg-white/10 hover:text-neutral-600 dark:hover:text-neutral-400 motion-reduce:transition-none'
      )}
    >
      <MagnifyingGlassIcon aria-hidden="true" className="size-4 shrink-0" />
      <span className="flex-1 truncate">Search</span>
      <span className="shrink-0 rounded border border-neutral-200 dark:border-neutral-700 px-1.5 py-0.5 text-[11px] leading-none text-neutral-400 dark:text-neutral-500">
        {shortcut}
      </span>
    </button>
  );
};

/** The collapsed rail's search icon, in the accent treatment the design gives it. */
export const SearchRailIcon = () => {
  const { open } = useSearchPalette();
  const shortcut = useShortcutLabel();

  return (
    <button
      type="button"
      onClick={open}
      title={`Search (${shortcut})`}
      className={clsx(
        'flex size-9 items-center justify-center rounded-lg text-neutral-500 dark:text-neutral-400',
        'transition-colors duration-150 ease-[cubic-bezier(0.2,0.8,0.2,1)] hover:bg-neutral-100 dark:hover:bg-white/10 hover:text-neutral-900 dark:hover:text-white',
        'motion-reduce:transition-none'
      )}
    >
      <span className="sr-only">Search</span>
      <MagnifyingGlassIcon aria-hidden="true" className="size-5" />
    </button>
  );
};
